import { useMemo } from "react";
import * as Styled from "./style";
import { isVideoLegacySeason, isVideoSeason, isVideoSpotLiveSeason } from "~/components/blocks/product/type-guard";
import { useQueryString } from "~/hooks/use-query-string";
import type { G_GetVideoQuery } from "~/lib/graphql";
import { G_VideoRatingCategory, G_VideoSeasonType } from "~/lib/graphql";

type ProductInfoTagProps = {
  videoData: G_GetVideoQuery["video"];
};

/**
 * 作品本来の情報を表示するコンポーネント
 * - グラビアの場合 : ユーザー評価を表示すること
 * - レビュー0件の場合 : ユーザー評価を表示しないこと（reviewSummary > reviewCommentCount が 0 の場合は表示しないこと）
 * - 公開年のデータない場合 : 公開年を表示しないこと
 * - 公開年のデータある場合 : 公開年を表示すること
 * - 収録時間のデータない場合 : 収録時間を表示しないこと
 * - 収録時間のデータある場合 and 単話の場合 : 収録時間を表示すること
 * - 映像品質のデータない場合 : 映像品質のデータを表示しないこと
 * - 映像品質のデータある場合 : 画角系 + 画質音質系 + 副音声 を表示すること
 * - レーティングのデータない場合 : レーティング情報を表示しないこと
 * - レーティングのデータある場合 : レーティング情報を表示すること
 */
export const ProductInfoTag = (props: ProductInfoTagProps) => {
  const { videoData } = props;

  const { seasonType, productionYear } = videoData;

  const { content } = useQueryString();

  const durationMinute = useMemo(() => {
    // エピソード末端の複数話の場合
    if (isVideoSeason(videoData) && content !== undefined) {
      // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
      if (!videoData.episode) return;
      const { episode } = videoData;
      return Math.round(episode.playInfo.duration / 60);
    }

    // エピソード末端でない複数話 もしくは 単数話の場合
    if (isVideoSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return;
      return Math.round(edges[0].node.playInfo.duration / 60);
    }

    if (isVideoLegacySeason(videoData)) {
      const {
        content: { playInfo },
      } = videoData;

      return Math.round(playInfo.duration / 60);
    }

    if (isVideoSpotLiveSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return;
      return Math.round(edges[0].node.playInfo.duration / 60);
    }

    return;
  }, [videoData]);

  const highestQuality = useMemo(() => {
    // エピソード末端の複数話の場合
    if (isVideoSeason(videoData) && content !== undefined) {
      // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
      if (!videoData.episode) return;
      const { episode } = videoData;
      return episode.playInfo.highestQuality;
    }

    // エピソード末端でない複数話 もしくは 単数話の場合
    if (isVideoSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return;
      return edges[0].node.playInfo.highestQuality;
    }

    if (isVideoLegacySeason(videoData)) {
      const {
        content: { playInfo },
      } = videoData;

      return playInfo.highestQuality;
    }

    if (isVideoSpotLiveSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return;
      return edges[0].node.playInfo.highestQuality;
    }

    return;
  }, [videoData]);

  const isSupportHDR = useMemo(() => {
    // エピソード末端の複数話の場合
    if (isVideoSeason(videoData) && content !== undefined) {
      // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
      if (!videoData.episode) return false;
      const { episode } = videoData;
      return episode.playInfo.isSupportHDR;
    }

    // エピソード末端でない複数話 もしくは 単数話の場合
    if (isVideoSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return false;
      return edges[0].node.playInfo.isSupportHDR;
    }

    if (isVideoLegacySeason(videoData)) {
      const {
        content: { playInfo },
      } = videoData;

      return playInfo.isSupportHDR;
    }

    if (isVideoSpotLiveSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return false;
      return edges[0].node.playInfo.isSupportHDR;
    }

    return false;
  }, [videoData]);

  const highestAudioChannelLayout = useMemo(() => {
    // エピソード末端の複数話の場合
    if (isVideoSeason(videoData) && content !== undefined) {
      // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
      if (!videoData.episode) return;
      const { episode } = videoData;
      return episode.playInfo.highestAudioChannelLayout;
    }

    // エピソード末端でない複数話 もしくは 単数話の場合
    if (isVideoSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return;
      return edges[0].node.playInfo.highestAudioChannelLayout;
    }

    if (isVideoLegacySeason(videoData)) {
      const {
        content: { playInfo },
      } = videoData;

      return playInfo.highestAudioChannelLayout;
    }

    if (isVideoSpotLiveSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return;
      return edges[0].node.playInfo.highestAudioChannelLayout;
    }

    return;
  }, [videoData]);

  if (seasonType === G_VideoSeasonType.Stage) return null;

  return (
    <Styled.DivSupplementBox>
      {/** レビュー */}
      {isVideoLegacySeason(videoData) && videoData.reviewSummary.averagePoint > 0 && (
        <Styled.RatingStar starScore={videoData.reviewSummary.averagePoint} isReadOnly />
      )}

      {/** 公開年 */}
      {productionYear !== undefined && productionYear !== null && (
        <Styled.ParText>{`${productionYear}年`}</Styled.ParText>
      )}

      {/** 収録時間 */}
      {(() => {
        if (seasonType === G_VideoSeasonType.SpotLive) return null;

        // 複数話のエピソード末端でない作品末端の場合は「収録時間」を表示しないこと
        if (seasonType === G_VideoSeasonType.MultiEpisodes && content === undefined) return null;

        if (durationMinute !== undefined && durationMinute !== 0) {
          return <Styled.ParText>{`${durationMinute}分`}</Styled.ParText>;
        }

        return null;
      })()}

      {/** 映像品質（画角系 : 4K / HQ / HD / SD）最も画質の高いものを表示する */}
      {highestQuality !== undefined && highestQuality !== "" && (
        <Styled.Tag tagText={highestQuality} borderColor="GRAY900" color="GRAY900" />
      )}

      {/** 画質音質系（HDR）*/}
      {isSupportHDR && <Styled.Tag tagText="HDR" borderColor="GRAY900" color="GRAY900" />}

      {/** 画質音質系（Dolby Atomos）*/}
      {highestAudioChannelLayout !== undefined && highestAudioChannelLayout !== "" && (
        <Styled.Tag tagText={highestAudioChannelLayout} borderColor="GRAY900" color="GRAY900" />
      )}

      {/** レーティング（NR / G / PG12 / R15 / R18+）*/}
      {(() => {
        const { category } = videoData.rating;

        if (category === G_VideoRatingCategory.Nr || category === G_VideoRatingCategory.R18Gravure) return null;

        if (category === G_VideoRatingCategory.R15) {
          return <Styled.Tag tagText="R15+" borderColor="GRAY900" color="GRAY900" />;
        }

        if (category === G_VideoRatingCategory.R18) {
          return <Styled.Tag tagText="R18+" borderColor="GRAY900" color="GRAY900" />;
        }

        return <Styled.Tag tagText={category} borderColor="GRAY900" color="GRAY900" />;
      })()}
    </Styled.DivSupplementBox>
  );
};
